import api from '@mysimpledb/shared/config/axios';

function toClientError(error, fallbackMessage) {
  const data = error?.response?.data || {};
  const clientError = new Error(data.error || error.message || fallbackMessage);
  clientError.code = data.code;
  clientError.status = error?.response?.status;
  return clientError;
}

export async function createContactRequest({
  recipientUserId,
  requestType,
  messageText,
  relatedEntryId,
}) {
  try {
    const { data } = await api.post('/messaging/contact-requests', {
      recipientUserId,
      requestType,
      messageText,
      relatedEntryId: relatedEntryId || null,
    });
    return data;
  } catch (error) {
    throw toClientError(error, 'Unable to send contact request.');
  }
}

export async function fetchConversations({ limit = 30, offset = 0 } = {}) {
  try {
    const { data } = await api.get('/messaging/conversations', {
      params: { limit, offset },
    });
    return data.conversations || [];
  } catch (error) {
    throw toClientError(error, 'Unable to load conversations.');
  }
}

export async function fetchMessages(
  conversationId,
  { limit = 50, beforeMessageId } = {}
) {
  try {
    const { data } = await api.get(
      `/messaging/conversations/${conversationId}/messages`,
      {
        params: {
          limit,
          ...(beforeMessageId ? { beforeMessageId } : {}),
        },
      }
    );
    return data.messages || [];
  } catch (error) {
    throw toClientError(error, 'Unable to load messages.');
  }
}

export async function acceptContactRequest(conversationId) {
  try {
    const { data } = await api.put(
      `/messaging/conversations/${conversationId}/accept`
    );
    return data.conversation;
  } catch (error) {
    throw toClientError(error, 'Unable to accept contact request.');
  }
}

export async function declineContactRequest(conversationId) {
  try {
    const { data } = await api.put(
      `/messaging/conversations/${conversationId}/decline`
    );
    return data.conversation;
  } catch (error) {
    throw toClientError(error, 'Unable to decline contact request.');
  }
}

export async function sendConversationMessage(conversationId, messageText) {
  try {
    const { data } = await api.post(
      `/messaging/conversations/${conversationId}/messages`,
      { messageText }
    );
    return data.message;
  } catch (error) {
    throw toClientError(error, 'Unable to send message.');
  }
}

export async function markConversationRead(conversationId) {
  try {
    const { data } = await api.put(
      `/messaging/conversations/${conversationId}/read`
    );
    return data;
  } catch (error) {
    throw toClientError(error, 'Unable to update message status.');
  }
}

export async function archiveConversation(conversationId) {
  try {
    const { data } = await api.put(
      `/messaging/conversations/${conversationId}/archive`
    );
    return data;
  } catch (error) {
    throw toClientError(error, 'Unable to archive conversation.');
  }
}

export async function unarchiveConversation(conversationId) {
  try {
    const { data } = await api.put(
      `/messaging/conversations/${conversationId}/unarchive`
    );
    return data;
  } catch (error) {
    throw toClientError(error, 'Unable to unarchive conversation.');
  }
}

export async function blockMessagingUser(userId) {
  try {
    const { data } = await api.post(`/messaging/blocks/${userId}`);
    return data;
  } catch (error) {
    throw toClientError(error, 'Unable to block user.');
  }
}

export async function unblockMessagingUser(userId) {
  try {
    const { data } = await api.delete(`/messaging/blocks/${userId}`);
    return data;
  } catch (error) {
    throw toClientError(error, 'Unable to unblock user.');
  }
}

export async function fetchUnreadMessageCount() {
  try {
    const { data } = await api.get('/messaging/conversations/unread-count');
    return Number(data.unreadCount) || 0;
  } catch (error) {
    throw toClientError(error, 'Unable to load unread message count.');
  }
}
